import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from "chart.js";
import { Grid } from "@mui/material";
import styles from "../../Styles/ComponentStyles/InflationCalculator/InflationCalculator.module.scss"
import React, { useEffect, useRef, useState } from "react";
import GlobalPopUp from "../../GlobalComponent/GlobalPopup/GlobalPopup";
import AnnuityReturnRateInputPopup from "./AnnuityReturnRateInputPopup";
import EditIcon from '@mui/icons-material/Edit';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface HistoricalDataPoint {
    year: number;
    rate: number;
    lastCalculatedMonth?: string;
}

interface FutureValueChartProps {
    data: any;
    amount: number;
    historicalData: HistoricalDataPoint[];
    years: number;
    staticRate: number;
    useHistoricalRates: boolean;
}

const FutureValueChart = ({ data, amount, historicalData, years, staticRate, useHistoricalRates }: FutureValueChartProps) => {
    // Add state for annuity return and checkbox
    const [isAnnuityLineVisible, setIsAnnuityLineVisible] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [annuityReturn, setAnnuityReturn] = useState(''); // for chart
    const [popupAnnuityReturn, setPopupAnnuityReturn] = useState(''); // for popup input

    // Handler for checkbox
    const handleAnnuityCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Only open the popup, do not check the checkbox yet
        if (!isAnnuityLineVisible) {
            setIsPopupOpen(true);
            setPopupAnnuityReturn(annuityReturn); // prefill popup with current chart value
        } else {
            // Uncheck and reset if already checked
            setIsAnnuityLineVisible(false);
            setAnnuityReturn("");
        }
    };

    // Handler for annuity return input change (in popup)
    const handleAnnuityReturnChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setPopupAnnuityReturn(e.target.value);
    };

    // Handler for Apply button in popup
    const handleApplyAnnuityReturn = () => {
        if (popupAnnuityReturn && !isNaN(Number(popupAnnuityReturn)) && Number(popupAnnuityReturn) !== 0) {
            setAnnuityReturn(popupAnnuityReturn);
            setIsAnnuityLineVisible(true);
        }
        setIsPopupOpen(false);
    };

    // Handler for closing popup without applying
    const handleClosePopup = () => {
        setIsPopupOpen(false);
        // Do not check the checkbox if not already checked
        if (!annuityReturn) {
            setIsAnnuityLineVisible(false);
        }
    };

    // Calculate values using historical rates
    const calculateHistoricalValues = () => {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - years;
        const historicalValues = [amount];
        const yearLabels = [startYear];
        const usedRates: (number | null)[] = [0]; // First year has 0 rate
        const months: (string | undefined)[] = [undefined];

        // Filter historical data for the relevant years
        const relevantHistoricalData = historicalData
            .filter(item => item.year >= startYear - 1 && item.year <= currentYear - 1)
            .sort((a, b) => a.year - b.year);

        let currentValue = amount;
        for (let i = 1; i <= years; i++) {
            const year = startYear + i;
            // Use previous year's rate for Jan YYYY
            const historicalRate = relevantHistoricalData.find(item => item.year === year - 1);
            const rateToUse = historicalRate?.rate || 0;
            currentValue = currentValue / (1 + rateToUse / 100);
            currentValue = Math.round(currentValue * 100) / 100;
            historicalValues.push(currentValue);
            yearLabels.push(year);
            usedRates.push(rateToUse);
            months.push(historicalRate?.lastCalculatedMonth);
        }

        return { values: historicalValues, labels: yearLabels, rates: usedRates, months };
    };

    // Calculate values using static rate
    const calculateStaticValues = () => {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - years;
        const staticValues = [amount];
        const yearLabels = [startYear];
        const usedRates: (number | null)[] = [0]; // First year has 0 rate
        let currentValue = amount;
        for (let i = 1; i <= years; i++) {
            currentValue = currentValue / (1 + staticRate / 100);
            currentValue = Math.round(currentValue * 100) / 100;
            staticValues.push(currentValue);
            yearLabels.push(startYear + i);
            usedRates.push(staticRate);
        }
        return { values: staticValues, labels: yearLabels, rates: usedRates };
    };

    // Function to calculate annuity-adjusted values using historical rates and annuity return
    const calculateAnnuityAdjustedValues = () => {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - years;

        const adjustedValues = [amount];
        let currentValue = amount;

        if (useHistoricalRates) {
            // Use historical inflation rates
            const relevantHistoricalData = historicalData
                .filter(item => item.year >= startYear - 1 && item.year <= currentYear - 1)
                .sort((a, b) => a.year - b.year);
            for (let i = 1; i <= years; i++) {
                const year = startYear + i;
                const inflationRate = relevantHistoricalData.find(item => item.year === year - 1)?.rate || 0;
                const annuityReturnNum = parseFloat(annuityReturn) || 0;
                const effectiveRate = annuityReturnNum - inflationRate;
                currentValue = currentValue * (1 + effectiveRate / 100);
                currentValue = Math.round(currentValue * 100) / 100;
                adjustedValues.push(currentValue);
            }
        } else {
            // Use static inflation rate
            const annuityReturnNum = parseFloat(annuityReturn) || 0;
            const effectiveRate = annuityReturnNum - staticRate;
            for (let i = 1; i <= years; i++) {
                currentValue = currentValue * (1 + effectiveRate / 100);
                currentValue = Math.round(currentValue * 100) / 100;
                adjustedValues.push(currentValue);
            }
        }
        return adjustedValues;
    };

    const historicalDataPoints = calculateHistoricalValues();
    const staticDataPoints = calculateStaticValues();
    const annuityAdjustedValues = calculateAnnuityAdjustedValues();

    const options: ChartOptions<"line"> = {
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 10,
                right: 10,
                top: 10,
                bottom: 10
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
                text: 'Erosion of Purchasing Power due to Inflation',
                font: { size: 20, weight: "500" },
                padding: {
                    top: 20,
                    bottom: 40
                },
                align: 'start'
            },
            tooltip: {
                backgroundColor: "rgba(255,255,255,0.95)",
                titleColor: "#101828",
                bodyColor: "#101828",
                borderColor: "#EAECF0",
                borderWidth: 1,
                padding: 10,
                callbacks: {
                    title: (context: any) => {
                        return context[0].label; // This will show "Jan YYYY"
                    },
                    label: (context: any) => {
                        const value = context.parsed.y;
                        const dataIndex = context.dataIndex;
                        const datasetLabel = context.dataset.label;
                        let rate = 0;
                        if (datasetLabel === "With Annuity Returns") {
                            // You can add annuity return info if you want
                            return [
                                `Adjusted Value w/ Returns: $${value.toLocaleString()}`,
                                `Inflation Rate: ${rate.toFixed(2)}%`
                            ];
                        } else {
                            if (useHistoricalRates) {
                                rate = historicalDataPoints.rates[dataIndex] || 0;
                            } else {
                                rate = staticRate;
                            }
                            return [
                                `Inflation-Adjusted Amount: $${value.toLocaleString()}`,
                                `Inflation Rate: ${rate.toFixed(2)}%`
                            ];
                        }
                    }
                },
                titleFont: { size: 16 },
                bodyFont: { size: 14 },
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    color: '#475467',
                    font: { size: 13 },
                    minRotation: 0,
                    maxRotation: 0,
                    autoSkip: true,
                    padding: 15,
                    callback: function (value: any) {
                        const label = this.getLabelForValue(value);
                        const match = label.match(/^([A-Za-z]+)\s+(\d{4})$/);
                        if (match) return [match[1], match[2]];
                        return label;
                    }
                },
                title: {
                    display: true,
                    text: 'Years',
                    color: '#475467',       // ← dark gray
                    // Chart.js FontSpec typing expects `weight` to be a string.
                    font: { size: 14, weight: '500' }
                }
            },
            y: {
                grid: {
                    drawBorder: false,
                    color: '#98A2B3',
                    borderDash: [4, 4],
                },
                ticks: {
                    color: '#475467',
                    font: { size: 13 },
                    padding: 15,
                    callback: function (value: any) {
                        return '$' + value.toLocaleString();
                    },
                },
                title: {
                    display: true,
                    text: 'Amount',
                    color: '#475467',
                    // Chart.js FontSpec typing expects `weight` to be a string.
                    font: { size: 14, weight: '500' }
                },
                grace: '2%',
            },
        },
        elements: {
            line: {
                borderWidth: 2,
            },
            point: {
                radius: 4,
                hoverRadius: 6,
            }
        }
    };

    // Combine both datasets
    const combinedData = {
        labels: data.labels,
        datasets: [
            {
                ...data.datasets[0],
                label: 'Inflation-Adjusted Amount',
                borderColor: '#4F6BFF',
                data: useHistoricalRates ? historicalDataPoints.values : staticDataPoints.values,
                pointBackgroundColor: '#4F6BFF',
                pointBorderColor: '#C7D2FE',
                pointBorderWidth: 4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#4F6BFF',
                pointHoverBorderColor: '#C7D2FE',
            },
            ...(isAnnuityLineVisible && annuityReturn
                ? [{
                    label: 'With Annuity Returns',
                    borderColor: '#F5CEA4',
                    borderDash: [6, 6],
                    data: annuityAdjustedValues,
                    pointBackgroundColor: '#F5CEA4',
                    pointBorderColor: '#fff',
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: '#F5CEA4',
                    pointHoverBorderColor: '#fff',
                }]
                : [])
        ]
    };

    return (
        <>
            {
                <GlobalPopUp
                    open={isPopupOpen}
                    onClose={handleClosePopup}
                    title="Compare with Annuity Returns"
                    showCloseIcon={true}
                    flagDivider
                    maxWidth={"xs"}
                    className={styles.rateOfReturnPopupTitle}
                    titleClassName={styles.rateOfReturnPopupTitle}
                >
                    <AnnuityReturnRateInputPopup
                        value={popupAnnuityReturn}
                        onChange={handleAnnuityReturnChange}
                        onApply={handleApplyAnnuityReturn}
                    />
                </GlobalPopUp>
            }
            <Grid container direction="column" gap="15px" overflow="auto">
                <Grid item xs={12} className={styles.futureChartCard}>
                    <Grid container className={styles.chartHeaderRow}>
                        <Grid item className={styles.chartTitleText}>
                            Erosion of buying power due to Inflation
                        </Grid>
                        <Grid item className={styles.chartHeaderRight}>
                            {isAnnuityLineVisible && annuityReturn ? (
                                <div className={styles.annuityActiveWrap} onClick={() => setIsPopupOpen(true)} role="button" tabIndex={0}>
                                    <input
                                        type="checkbox"
                                        checked={isAnnuityLineVisible}
                                        onChange={handleAnnuityCheckboxChange}
                                    />
                                    <span className={styles.annuityActiveText}>
                                        Rate of Return: <span>{annuityReturn}%</span>
                                    </span>
                                    <EditIcon className={styles.annuityEditIcon} />
                                </div>
                            ) : (
                                <label className={styles.compareLabel}>
                                    <input
                                        type="checkbox"
                                        checked={isAnnuityLineVisible}
                                        onChange={handleAnnuityCheckboxChange}
                                    />
                                    Compare with Annuity Returns
                                </label>
                            )}
                        </Grid>
                    </Grid>
                    <Grid container overflow="auto" className={styles.chartCanvasOuter}>
                        <Grid item xs={12} className={styles.chartCanvasWrap}>
                            <Line
                                data={combinedData}
                                options={options}
                            />
                        </Grid>
                    </Grid>
                    <Grid item className={styles.erosionChartMsgText}>
                        This chart shows how inflation makes your money worth less over time. Turning on the annuity return line lets you see how steady investment returns can help your money keep its value or even grow despite inflation.
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
};

export default FutureValueChart;